import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useAuth } from '@/core/auth/AuthContext';
import { Input, Button } from '@/components/ui';

const loginSchema = z.object({
  email: z.string().email('Email không hợp lệ'),
  password: z.string().min(6, 'Mật khẩu phải từ 6 ký tự'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export function InternalLoginForm() {
  const { signIn, isLoading } = useAuth();
  const [error, setError] = React.useState<string | null>(null);

  const { register, handleSubmit, formState: { errors } } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormValues) => {
    setError(null);
    try {
      const { error: signInError } = await signIn(data.email, data.password);
      if (signInError) {
        setError(signInError);
      }
    } catch (err: any) {
      setError(err.message || 'Đăng nhập thất bại. Vui lòng kiểm tra lại.');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      {error && (
        <div className="p-3 rounded-xl bg-red-50 border border-red-100 flex items-center gap-2 animate-in fade-in slide-in-from-top-1">
          <i className="ri-error-warning-line text-red-500 text-lg" />
          <p className="text-xs text-red-600 font-bold">{error}</p>
        </div>
      )}

      <div className="space-y-1.5">
        <label className="text-xs font-black text-slate-400  tracking-widest px-1">Email nội bộ</label>
        <div className="relative group">
          <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-brand-600 transition-colors" />
          <input
            {...register('email')}
            type="email"
            placeholder="name@landscape.vn"
            className="w-full h-12 pl-11 pr-4 bg-slate-50/50 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-4 focus:ring-brand-600/10 focus:border-brand-600 transition-all"
          />
        </div>
        {errors.email && <p className="text-[10px] text-red-500 font-bold px-1">{errors.email.message}</p>}
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between px-1">
          <label className="text-xs font-black text-slate-400  tracking-widest">Mật khẩu</label>
        </div>
        <div className="relative group">
          <i className="ri-lock-line absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-brand-600 transition-colors" />
          <input
            {...register('password')}
            type="password"
            placeholder="••••••••"
            className="w-full h-12 pl-11 pr-4 bg-slate-50/50 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-4 focus:ring-brand-600/10 focus:border-brand-600 transition-all"
          />
        </div>
        {errors.password && <p className="text-[10px] text-red-500 font-bold px-1">{errors.password.message}</p>}
      </div>


      <Button
        type="submit"
        isLoading={isLoading}
        className="w-full h-12 bg-brand-600 hover:bg-brand-500 text-white rounded-xl font-bold shadow-lg shadow-brand-600/20 transition-all"
      >
        Đăng nhập hệ thống
      </Button>
    </form>
  );
}
